import { vTooltip } from 'floating-vue';
import log from 'loglevel';
import { configureCompat, createApp } from 'vue';
import VueMatomo from 'vue-matomo';
import VueZoomer from 'vue-zoomer';
import SmartTable from 'vuejs-smart-table';

import App from '@/App.vue';
import { AppName } from '@/data/Branding';
import { perfLog, perfMsg } from '@/data/log/PerformanceLog';
import DataWorker from '@/data/storage/DataWorker';
import router from '@/router';
import pinia from '@/stores';

import Integrations from './data/config/Integrations';
import ObjectStoreSync from './data/storage/ObjectStoreSync';

// TODO: remove the following feature compatibility config once fully migrated to Vue 3
// Note default is Vue 3 behaviour with Vue 2 compatibility enabled for particular features
configureCompat({
  MODE: 3,
  GLOBAL_SET: true,
  GLOBAL_DELETE: true,
  WATCH_ARRAY: true,
  ATTR_FALSE_VALUE: true,
  COMPONENT_V_MODEL: true,
});

window.fenderLoadStart = performance.now();
perfLog.debug(perfMsg(`Starting ${AppName} load: ${window.fenderLoadStart}`));

document.addEventListener('DOMContentLoaded', () => {
  perfLog.debug(perfMsg('DOM content loaded', true, window.pageLoadStart));
});
window.addEventListener('load', () => {
  perfLog.debug(perfMsg('Window loaded', true, window.pageLoadStart));
});

declare global {
  interface Window {
    fenderLoadStart: number;
  }

  interface Document {
    webkitFullscreenElement: HTMLDivElement;
    webkitExitFullscreen?: () => Promise<void>;
  }

  interface HTMLElement {
    webkitRequestFullscreen?: () => Promise<void>;
  }
}

// Polyfill withResolvers if necessary (used by latest versions of pdfjs-dist)
// @ts-expect-error Since it is provided by this polyfill
if (typeof Promise.withResolvers === 'undefined') {
  if (window) {
    // @ts-expect-error Since it is provided by this polyfill
    window.Promise.withResolvers = function () {
      let resolve, reject;
      const promise = new Promise((_resolve, _reject) => {
        resolve = _resolve;
        reject = _reject;
      });
      return {
        promise,
        resolve,
        reject
      };
    };
  }
}

perfLog.debug(perfMsg('Loading pre-requisites', true));

// Start out data loading from the indexed DB
ObjectStoreSync.getInstance().then((objectStoreSync: ObjectStoreSync) => {
  objectStoreSync.reloadAll();
});

const waitingForInstance: number = performance.now();
DataWorker.waitForInstance().then(() => {
  perfLog.debug(perfMsg('Data worker loaded', true, waitingForInstance));
});

const mounting: number = performance.now();
perfLog.debug(perfMsg('Pre-requisites loaded', true, window.pageLoadStart));

export const app = createApp(App);

app.config.globalProperties.$appName = AppName;
// Turning this on adds Vue timing events to the browser dev tools performance tab
// app.config.performance = true;

// https://v3-migration.vuejs.org/breaking-changes/global-api.html#config-productiontip-removed
// > In Vue 3.x, the "use production build" tip will only show up when using the "dev + full build"
// Vue.config.productionTip = false;

app.use(pinia);
app.use(router);
app.use(SmartTable);
app.use(VueZoomer);
if (Integrations.ANALYTICS_SERVER) {
  app.use(VueMatomo, {
    host: Integrations.ANALYTICS_SERVER,
    siteId: 1,
    enableHeartBeatTimer: true,
    heartBeatTimerInterval: 15, // in seconds
    trackPageView: false, // controlled by our code
  });
} else {
  log.debug('Analytics not configured, skipping initialisation');
}
app.directive('tooltip', vTooltip);
app.mount('#app');

// Declare the _paq variable that is used by Matomo as its queueing buffer.
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _paq: any;
  }
}
window._paq = window._paq || [];

perfLog.debug(perfMsg('App mounted', false, mounting));
perfLog.debug(perfMsg('Total load', false, window.pageLoadStart));
